import { createReducer } from '@reduxjs/toolkit'
import { SpotOrderStatus } from 'api/v1/spot'
import { differenceBy, uniqBy } from 'lodash'
import {
  cleanUserSpotDataAction,
  updateCancelAllSpotAction,
  updateCancelSpotPendingIds,
  updateCreateOrderOption,
  updateCurrentSpotSymbol,
  updateLastInputAmountType,
  updateOrderDirection,
  updateSpotBadgeOrderAction,
  updateSpotBadgeTradeAction,
  updateSpotCurrentOrderTab,
  updateSpotMatchRecordAction,
  updateSpotOrderRecordAction,
  updateSpotPostOnlyAction,
  updateSpotSelectedOrderMode,
} from './actions'
import {
  LastInputAmountType,
  OrderDirection,
  OrderModeNames,
  SpotOrderTabType,
  SpotState,
} from './types'
import { getSpotClosedOrdersAction } from './actions/getSpotClosedOrdersActions'
import { getSpotMatchClearingsAction } from './actions/getSpotMatchClearingsActions'
import { getSpotFeeRatesAction } from './actions/getSpotFeeRatesActions'
import { getSpotOpenOrdersAction } from './actions/getSpotOpenOrdersActions'

export const initialState: SpotState = {
  currentSpotSymbol: 0,
  openOrders: [],
  badgeOrder: false,
  badgeTrade: false,
  closedOrders: {
    list: [],
    isLastPage: false,
    offsetId: 0,
  },
  matchClearings: {
    list: [],
    isLastPage: false,
    offsetId: 0,
  },
  feeRates: undefined,
  createOrderOption: {
    limitPrice: '',
    baseCurrencyAmount: '',
    quoteCurrencyAmount: '',
  },
  orderMode: OrderModeNames.Limit,
  lastInputAmountType: LastInputAmountType.BaseCurrency,
  orderDirection: OrderDirection.LONG,
  cancelSpotPendingIds: [],
  cancelAllOpenSpotState: false,
  orderTab: SpotOrderTabType.OpenOrder,
  postOnly: false,
}

export default createReducer<SpotState>(initialState, (builder) => {
  builder
    .addCase(updateCurrentSpotSymbol, (state, { payload }) => {
      state.currentSpotSymbol = payload
    })
    .addCase(getSpotOpenOrdersAction.fulfilled, (state, { payload }) => {
      state.openOrders = payload
    })
    .addCase(updateSpotBadgeOrderAction, (state, { payload }) => {
      state.badgeOrder = payload
    })
    .addCase(updateSpotBadgeTradeAction, (state, { payload }) => {
      state.badgeTrade = payload
    })
    .addCase(updateSpotOrderRecordAction, (state, { payload }) => {
      switch (payload.type) {
        case 'order_pending':
          state.openOrders = uniqBy([payload.data, ...state.openOrders], 'id')
          break

        case 'order_filled':
          // FULLY_FILLED: remove record from open orders
          const isFullyFilled =
            payload.data.status === SpotOrderStatus.FULLY_FILLED
          if (isFullyFilled) {
            state.openOrders = state.openOrders.filter(
              (v) => v.id !== payload.data.id,
            )
            // Add records to closed orders
            state.closedOrders.list = uniqBy(
              [payload.data, ...state.closedOrders.list],
              'id',
            )
          }
          // PARTIAL_FILLED: update record in  open order
          else {
            const index = state.openOrders.findIndex(
              (v) => v.id === payload.data.id,
            )
            if (index > -1) {
              state.openOrders[index] = payload.data
            }
          }
          break

        case 'order_cancelled':
        case 'order_failed':
          const orders = state.openOrders.filter(
            (v) => v.id !== payload.data.id,
          )
          state.openOrders = orders

          // Add records to closed orders
          state.closedOrders.list = uniqBy(
            [payload.data, ...state.closedOrders.list],
            'id',
          )
          break
      }
    })
    .addCase(cleanUserSpotDataAction, (state) => {
      state.openOrders = []
      state.badgeOrder = false
      state.badgeTrade = false
      state.closedOrders = {
        list: [],
        isLastPage: false,
        offsetId: 0,
      }
      state.matchClearings = {
        list: [],
        isLastPage: false,
        offsetId: 0,
      }
      state.cancelSpotPendingIds = []
      state.cancelAllOpenSpotState = false
    })
    .addCase(getSpotFeeRatesAction.fulfilled, (state, { payload }) => {
      state.feeRates = payload
    })
    .addCase(updateCreateOrderOption, (state, { payload }) => {
      state.createOrderOption[payload.option] = payload.value
    })
    .addCase(updateSpotSelectedOrderMode, (state, { payload }) => {
      state.orderMode = payload
    })
    .addCase(updateLastInputAmountType, (state, { payload }) => {
      state.lastInputAmountType = payload
    })
    .addCase(updateOrderDirection, (state, { payload }) => {
      state.orderDirection = payload
    })
    .addCase(updateCancelSpotPendingIds, (state, { payload }) => {
      state.cancelSpotPendingIds = payload
    })
    .addCase(updateCancelAllSpotAction, (state, { payload }) => {
      state.cancelAllOpenSpotState = payload
    })
    .addCase(updateSpotCurrentOrderTab, (state, { payload }) => {
      state.orderTab = payload
    })
    .addCase(getSpotClosedOrdersAction.fulfilled, (state, { payload }) => {
      const { list, isLastPage, offsetId } = payload
      // if (!list.length) {
      //   return
      // }
      state.closedOrders.list = list
      state.closedOrders.isLastPage = isLastPage
      state.closedOrders.offsetId = offsetId
    })
    .addCase(getSpotMatchClearingsAction.fulfilled, (state, { payload }) => {
      const { list, isLastPage, offsetId } = payload
      if (!list.length) {
        return
      }
      state.matchClearings.list = list
      state.matchClearings.isLastPage = isLastPage
      state.matchClearings.offsetId = offsetId
    })
    .addCase(updateSpotMatchRecordAction, (state, { payload }) => {
      state.matchClearings.list = uniqBy(
        [payload.data, ...state.matchClearings.list],
        'txHash',
      )
    })
    .addCase(updateSpotPostOnlyAction, (state, { payload }) => {
      state.postOnly = payload
    })
})
